import {
  COVERAGE_CLASSES,
  coverageDisplayText,
  coverageFromCounts,
  getBiasCategory,
} from '@/utils/bias'

// Counts from whichever shape the caller has: the feed carries `lean`, the
// story page and the edition carry `articles`.
export function countsOf(node) {
  if (node?.lean) {
    return [node.lean.left || 0, node.lean.center || 0, node.lean.right || 0]
  }
  let left = 0
  let center = 0
  let right = 0
  for (const a of node?.articles ?? []) {
    const key = getBiasCategory(a.outlet?.bias ?? 0)
    if (key === 'left') left += 1
    else if (key === 'right') right += 1
    else center += 1
  }
  return [left, center, right]
}

/**
 * The one definition of a story's selection label — text, colour AND key —
 * used by every surface that shows it and by the feed's Left/Right tabs to
 * decide membership. Exported because a tab that filters on one rule while
 * the row prints another is how you end up with a "Balanced" chip under a
 * tab called Right.
 *
 * Prefers the pipeline's stored label (bias_utils.coverage_label_from_counts,
 * the single source of truth) and falls back to computing from counts for
 * rows not yet backfilled.
 */
export function selectionCoverage(node) {
  let key = node?.coverage_color || null
  let text = node?.coverage_label
    ? coverageDisplayText(node.coverage_label)
    : null
  if (!key || !text) {
    const [left, center, right] = countsOf(node)
    const computed = coverageFromCounts(left, center, right)
    key = computed.key
    text = coverageDisplayText(computed.text)
  }
  if (!text || key === 'none' || text === 'No sources') return null

  return { key, text, color: COVERAGE_CLASSES[key] ?? COVERAGE_CLASSES.none }
}

// The topic's SELECTION label — outlet lean across ALL of its articles
// ("Mostly Left", "Leans Right", "Balanced"...), coloured with the locked
// vocabulary: blue or red when a side leads, grey otherwise.
export function SelectionLabel({ node, className = '', colored = false }) {
  const sel = selectionCoverage(node)
  if (!sel) return null
  return (
    // Sentence case, like every other fact on the page — it follows .byline's
    // voice, not .panel-eyebrow's.
    //
    // Ink by default (the mocks, 2026-11-01; muted before): wherever this
    // label appears it sits beside a LeanBar, and one fact should not be
    // encoded twice. Hue is the graph's
    // job; the words state the verdict. Colouring both made the label compete
    // with the mark that carries the same information more precisely.
    // `colored` restores the lean hue for a surface with no bar beside it.
    <span
      className={`font-sans text-xs font-semibold ${
        colored ? sel.color : 'text-foreground'
      } ${className}`}
      title="Pano’s rating of who covered it"
    >
      {sel.text}
    </span>
  )
}
